import React from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, type BreadcrumbItem } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { revokeRefreshTokens, type RevokeRefreshTokensResponse } from '../services/authOperationsService';
import styles from './RefreshTokenOperationsPage.module.css';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

export const RefreshTokenOperationsPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { auth } = useAuth();

  const [employeeId, setEmployeeId] = React.useState('');
  const [submitting, setSubmitting] = React.useState<'tenant' | 'employee' | null>(null);
  const [confirmTenantRevokeOpen, setConfirmTenantRevokeOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<RevokeRefreshTokensResponse | null>(null);

  const trimmedEmployeeId = employeeId.trim();
  const hasEmployeeId = trimmedEmployeeId.length > 0;
  const employeeIdValid = !hasEmployeeId || GUID_PATTERN.test(trimmedEmployeeId);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('admin', { ns: 'admin' }), path: '/admin' },
    { label: 'Auth tools', path: '/admin/auth/revoke-refresh-tokens' },
  ];

  const submit = async (scope: 'tenant' | 'employee') => {
    setSubmitting(scope);
    setError(null);
    setResult(null);

    try {
      const response = await revokeRefreshTokens(scope === 'employee'
        ? { employeeId: trimmedEmployeeId }
        : {});
      setResult(response);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to revoke refresh tokens.');
    } finally {
      setSubmitting(null);
    }
  };

  const confirmTenantRevoke = async () => {
    setConfirmTenantRevokeOpen(false);
    await submit('tenant');
  };

  return (
    <section className={styles.page}>
      <Breadcrumb items={breadcrumbItems} />

      <div className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>Auth tools</p>
          <h1 className={styles.title}>Refresh token revocation</h1>
          <p className={styles.subtitle}>
            Use this page to revoke silent-renewal sessions without direct SQL access. Keep it URL-only for now.
          </p>
        </div>
        <div className={styles.metaCard}>
          <span className={styles.metaLabel}>Signed in as</span>
          <strong className={styles.metaValue}>{auth?.username || 'Unknown admin'}</strong>
          <span className={styles.metaHint}>Current tenant-scoped admin action</span>
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>Revoke sessions</h2>
          <p className={styles.panelText}>
            Leave the employee ID empty to revoke every active refresh token for the current tenant.
          </p>

          <label className={styles.label} htmlFor="employeeId">Employee ID</label>
          <input
            id="employeeId"
            className={styles.input}
            type="text"
            value={employeeId}
            onChange={(event) => setEmployeeId(event.target.value)}
            placeholder="Optional employee GUID"
            autoComplete="off"
            spellCheck={false}
          />
          <p className={styles.helperText}>
            {hasEmployeeId
              ? employeeIdValid
                ? 'Valid GUID format. Use the employee action to revoke only this user.'
                : 'Enter a valid GUID before using the employee action.'
              : 'No employee selected. The tenant action will revoke all refresh tokens in this tenant.'}
          </p>

          <div className={styles.actions}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitting !== null}
              onClick={() => setConfirmTenantRevokeOpen(true)}
            >
              {submitting === 'tenant' ? 'Revoking tenant sessions...' : 'Revoke tenant sessions'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={submitting !== null || !hasEmployeeId || !employeeIdValid}
              onClick={() => void submit('employee')}
            >
              {submitting === 'employee' ? 'Revoking user sessions...' : 'Revoke user sessions'}
            </button>
          </div>

          {error ? <div className={styles.errorBanner}>{error}</div> : null}

          {result ? (
            <div className={styles.resultCard}>
              <div className={styles.resultHeader}>
                <h3 className={styles.resultTitle}>Revocation completed</h3>
                <span className={styles.resultBadge}>{result.scope}</span>
              </div>

              <dl className={styles.resultGrid}>
                <div>
                  <dt>Revoked tokens</dt>
                  <dd>{result.revokedCount}</dd>
                </div>
                <div>
                  <dt>Revoked at</dt>
                  <dd>{formatDateTime(result.revokedAtUtc)}</dd>
                </div>
                <div>
                  <dt>Employee ID</dt>
                  <dd>{result.employeeId || 'Tenant-wide action'}</dd>
                </div>
              </dl>
            </div>
          ) : null}
        </div>

        <aside className={styles.sidePanel}>
          <h2 className={styles.panelTitle}>Operational note</h2>
          <ul className={styles.noteList}>
            <li>Refresh-token revocation blocks future silent renewals only.</li>
            <li>Existing access tokens remain valid until they expire.</li>
            <li>With current defaults, that remaining access-token window is up to 60 minutes.</li>
            <li>For immediate cut-off, you still need a broader auth strategy than stateless JWT expiry alone.</li>
          </ul>
        </aside>
      </div>

      {confirmTenantRevokeOpen ? (
        <div className={styles.modalBackdrop} role="presentation" onClick={() => setConfirmTenantRevokeOpen(false)}>
          <div
            className={styles.modalCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tenant-revoke-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p className={styles.modalEyebrow}>Confirm tenant-wide action</p>
            <h2 id="tenant-revoke-title" className={styles.modalTitle}>Revoke all tenant refresh tokens?</h2>
            <p className={styles.modalText}>
              This will revoke silent-renewal sessions for every user in the current tenant. Existing access tokens can still work until they expire.
            </p>
            <ul className={styles.modalList}>
              <li>All current tenant mobile sessions will lose refresh capability.</li>
              <li>Users will need to log in again after their active access token expires.</li>
              <li>This action does not affect other tenants.</li>
            </ul>
            <div className={styles.modalActions}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={submitting !== null}
                onClick={() => setConfirmTenantRevokeOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={submitting !== null}
                onClick={() => void confirmTenantRevoke()}
              >
                {submitting === 'tenant' ? 'Revoking tenant sessions...' : 'Confirm tenant revoke'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default RefreshTokenOperationsPage;